import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Picker from "react-month-picker";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { BuildYears, convertToMoment, MakeMonthName, MonthNames } from "modules/common/models/period";
import styles from "./PeriodRangeSlider.module.scss";

interface PeriodRangeSliderProps {
    month: number;
    year: number;
    /** Сколько месяцев показывается на странице */
    monthsCount: number;
    onChange: (year: number, month: number) => void;
}

/**
 * Переключатель периода для страницы плана: на странице сразу несколько месяцев,
 * поэтому в заголовке они перечислены через тире — «Август — Сентябрь 2026».
 */
export const PeriodRangeSlider: React.FC<PeriodRangeSliderProps> = (props) => {
    const { month, year, monthsCount, onChange } = props;

    const [isOpen, setIsOpen] = useState(false);
    const pickerRef = useRef<any>(undefined);

    const gotoNextMonth = useCallback(() => {
        const next = convertToMoment(month, year).add(1, "month");
        onChange(next.year(), next.month() + 1);
    }, [month, year, onChange]);

    const gotoPreviousMonth = useCallback(() => {
        const prev = convertToMoment(month, year).subtract(1, "month");

        if (prev.isValid()) {
            onChange(prev.year(), prev.month() + 1);
        }
    }, [month, year, onChange]);

    const onMonthChange = useCallback(
        (y: number, m: number) => {
            onChange(y, m);
            setIsOpen(false);
        },
        [onChange]
    );

    const togglePicker = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    }, []);

    // react-month-picker 2 открывает окно только методом show() — см. PeriodSlider
    useEffect(() => {
        isOpen && pickerRef.current && pickerRef.current.show();
    }, [isOpen]);

    const onDismiss = useCallback(() => setIsOpen(false), []);

    const yearsOptions = useMemo(() => {
        try {
            return BuildYears(year) || [year];
        } catch {
            return [year];
        }
    }, [year]);

    const title = useMemo(() => {
        const start = convertToMoment(month, year);
        const end = convertToMoment(month, year).add(monthsCount - 1, "month");

        // перечислять все месяцы незачем — период задают его края
        const first = MakeMonthName(start.month() + 1);
        const last = MakeMonthName(end.month() + 1);

        return {
            months: monthsCount > 1 ? `${first} — ${last}` : first,
            years: start.year() === end.year() ? `${start.year()}` : `${start.year()} — ${end.year()}`,
        };
    }, [month, year, monthsCount]);

    return (
        <div className={`period-slider ${styles.slider}`} onClick={(e) => e.stopPropagation()}>
            <PlanrButton type="neutral" icon="general-big-arrow-left" onClick={gotoPreviousMonth} size="small" />

            <div className={styles.monthName} onClick={togglePicker}>
                <span className={styles.value}>&nbsp;{title.months}&nbsp;</span>
                <div className={styles.year}>{title.years}</div>

                {isOpen && (
                    // щелчки внутри окна не всплывают до заголовка — см. PeriodSlider
                    <span style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
                        <Picker
                            ref={pickerRef}
                            years={yearsOptions}
                            value={{ month, year }}
                            lang={MonthNames}
                            onChange={onMonthChange}
                            onDismiss={onDismiss}
                        />
                    </span>
                )}
            </div>

            <PlanrButton type="neutral" icon="general-big-arrow-right" onClick={gotoNextMonth} size="small" />
        </div>
    );
};
